import { db } from "@/lib/firebase";
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import type { DirectoryArea, DirectoryCity } from "@/types/location";

function normalizeText(value: unknown) {
  return String(value ?? "").trim().toLowerCase();
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asNullableString(value: unknown, fallback: string | null = null) {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function includesSearch(text: unknown, search: string) {
  if (!search) return true;
  return normalizeText(text).includes(search);
}

function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Load all public cities from:
 * counties/{countyId}/cities/{cityId}
 */
export async function getPublicCities(
  searchText?: string
): Promise<DirectoryCity[]> {
  const normalizedSearch = normalizeText(searchText);

  const q = query(
    collectionGroup(db, "cities"),
    where("isDeleted", "==", false)
  );

  const snap = await getDocs(q);

  const rows: DirectoryCity[] = snap.docs.map((docSnap) => {
    const data = docSnap.data() as Record<string, unknown>;

    const parentCountyRef = docSnap.ref.parent.parent;

    return {
      id: docSnap.id,
      name: asString(data.name),
      nameLower: normalizeText(data.nameLower ?? data.nameNormalized ?? data.name),

      // Important: infer countyId from the document path if the field is missing.
      countyId: asNullableString(data.countyId, parentCountyRef?.id ?? null),
      countyName: asNullableString(data.countyName),
    };
  });

  return sortByName(
    rows.filter(
      (row) =>
        !!row.name &&
        includesSearch(row.nameLower ?? row.name, normalizedSearch)
    )
  );
}

/**
 * Load public cities only after customer has searched.
 */
export async function getPublicCitiesForSearch(
  searchText: string
): Promise<DirectoryCity[]> {
  const normalizedSearch = normalizeText(searchText);

  if (!normalizedSearch) return [];

  return getPublicCities();
}

/**
 * Load areas for a selected city.
 *
 * Preferred path:
 * counties/{countyId}/cities/{cityId}/areas/{areaId}
 *
 * Fallback:
 * collectionGroup("areas") then filter in memory.
 */
export async function getPublicAreasByCity(
  cityId: string | null | undefined,
  countyId?: string | null,
  searchText?: string
): Promise<DirectoryArea[]> {
  const cleanCityId = String(cityId ?? "").trim();
  const cleanCountyId = String(countyId ?? "").trim();
  const normalizedSearch = normalizeText(searchText);

  if (!cleanCityId) return [];

  const q = cleanCountyId
    ? query(
        collection(
          db,
          "counties",
          cleanCountyId,
          "cities",
          cleanCityId,
          "areas"
        ),
        where("isDeleted", "==", false)
      )
    : query(
        collectionGroup(db, "areas"),
        where("isDeleted", "==", false)
      );

  const snap = await getDocs(q);

  const rows: DirectoryArea[] = snap.docs.map((docSnap) => {
    const data = docSnap.data() as Record<string, unknown>;

    const cityRef = docSnap.ref.parent.parent;
    const countyRef = cityRef?.parent.parent;

    return {
      id: docSnap.id,
      name: asString(data.name),
      nameLower: normalizeText(data.nameLower ?? data.nameNormalized ?? data.name),

      countyId: asNullableString(data.countyId, countyRef?.id ?? cleanCountyId ?? null),
      countyName: asNullableString(data.countyName),

      cityId: asNullableString(data.cityId, cityRef?.id ?? cleanCityId),
      cityName: asNullableString(data.cityName),
    };
  });

  return sortByName(
    rows.filter((row) => {
      const matchesCity = row.cityId === cleanCityId;
      const matchesSearch = includesSearch(
        row.nameLower ?? row.name,
        normalizedSearch
      );

      return !!row.name && matchesCity && matchesSearch;
    })
  );
}